import { supabase } from '../supabaseClient';

/**
 * Fetch only the fields needed for revenue charting.
 * Excludes voided invoices at the query level.
 */
export const fetchRevenueData = async () => {
  const { data, error } = await supabase
    .from('invoices')
    .select('invoice_date, total_amount')
    .eq('is_void', false);

  if (error) throw error;
  return data || [];
};

/**
 * Fetch all the data needed for the dashboard summary cards:
 * Revenue (current & previous month), Bills (current month),
 * Receivable (open invoices minus payments), Payable (open bills).
 */
export const fetchDashboardSummary = async () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();

  const pad = (n) => String(n).padStart(2, '0');
  const monthStart = `${y}-${pad(m + 1)}-01`;
  const nextMonthStart =
    m === 11 ? `${y + 1}-01-01` : `${y}-${pad(m + 2)}-01`;
  const prevMonthStart =
    m === 0 ? `${y - 1}-12-01` : `${y}-${pad(m)}-01`;

  const [curInv, prevInv, curBills, openInv, openBills, payments] =
    await Promise.all([
      supabase
        .from('invoices')
        .select('total_amount')
        .eq('is_void', false)
        .gte('invoice_date', monthStart)
        .lt('invoice_date', nextMonthStart),
      supabase
        .from('invoices')
        .select('total_amount')
        .eq('is_void', false)
        .gte('invoice_date', prevMonthStart)
        .lt('invoice_date', monthStart),
      supabase
        .from('bill')
        .select('total_amount')
        .gte('bill_date', monthStart)
        .lt('bill_date', nextMonthStart),
      supabase
        .from('invoices')
        .select('invoice_id, total_amount')
        .eq('is_void', false)
        .eq('is_closed', false),
      supabase
        .from('bill')
        .select('bill_id, total_amount')
        .eq('is_closed', false),
      supabase
        .from('payments')
        .select('invoice_id, payment_amount'),
    ]);

  const sum = (rows) =>
    (rows?.data || []).reduce(
      (s, r) => s + parseFloat(r.total_amount || 0),
      0,
    );

  const revenue = sum(curInv);
  const prevRevenue = sum(prevInv);
  const bills = sum(curBills);

  const paymentsByInvoice = {};
  (payments.data || []).forEach((p) => {
    paymentsByInvoice[p.invoice_id] =
      (paymentsByInvoice[p.invoice_id] || 0) +
      parseFloat(p.payment_amount || 0);
  });

  let receivable = 0;
  let invoicesDueCount = 0;
  (openInv.data || []).forEach((inv) => {
    const paid = paymentsByInvoice[inv.invoice_id] || 0;
    const due = Math.max(parseFloat(inv.total_amount || 0) - paid, 0);
    if (due > 0) {
      receivable += due;
      invoicesDueCount++;
    }
  });

  const payable = sum(openBills);
  const billsDueCount = (openBills.data || []).length;

  const monthLabel = now.toLocaleString('default', { month: 'short' }).toUpperCase();

  return {
    revenue,
    prevRevenue,
    bills,
    receivable,
    payable,
    invoicesDueCount,
    billsDueCount,
    monthLabel,
  };
};
